import React, { useMemo } from 'react';
import { H3, KPIContent, MetricsWrapper } from './shared.style';
import KPIRedesignedListItem from './KPIListItem';
import { TWTDText, DateRange } from '../shared';
import ContentLoader from 'components/utility/ContentLoader/ContentLoader';
import KPIListLoader from './KPIListLoader';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import Box from 'components/elements/Box';

import useDateRangeKPI from 'hooks/useDateRangeKPI';
import { tooltipContent } from 'helpers/getTooltipContent';
import formatDate from 'helpers/formatDate';

interface IPlaylistKPIListProps {
  mappedPlaylistData: any;
  isMobileView: boolean;
  error?: string;
}

const PlaylistKPIList: React.FC<IPlaylistKPIListProps> = (props) => {
  const { mappedPlaylistData, isMobileView, error } = props;

  const [thisWeekToDate, lastWeek] = useDateRangeKPI('playlist');

  const playlistKeyValueGroupItems = useMemo(() => {
    // key-value group items
    if (mappedPlaylistData)
      return [
        {
          label: 'Streams',
          metric: mappedPlaylistData?.playlist_stream_count_this_week_to_date,
        },
        {
          label: 'Previous',
          metric: mappedPlaylistData?.playlist_stream_count_last_week_to_date,
        },
        {
          label: 'Change',
          metric: mappedPlaylistData?.playlist_stream_wow_in_progress,
        },
      ];
  }, [mappedPlaylistData]);

  const playlistLWTDItems = useMemo(() => {
    if (mappedPlaylistData)
      return [
        {
          label: 'Streams',
          metric: mappedPlaylistData?.playlist_stream_count_last_week_to_date,
        },
      ];
  }, [mappedPlaylistData]);

  const playlistCountSkiprateKeyValueGroupItems = useMemo(() => {
    // key-value group items
    if (mappedPlaylistData)
      return [
        {
          label: 'Playlist count',
          metric: mappedPlaylistData?.playlist_count,
        },
        {
          label: 'Skip rate',
          metric: mappedPlaylistData?.skiprate_this_week_to_date,
        },
      ];
  }, [mappedPlaylistData]);

  return (
    <KPIContent isMobileView={isMobileView}>
      <Box display="flex" flexDirection={isMobileView ? 'column' : 'row'}>
        <Box flexBasis="50%" mb={isMobileView ? '3rem' : 0}>
          {playlistKeyValueGroupItems && !error ? (
            <H3 isMobileView={isMobileView}>
              <TippyToolTip
                enabled
                tooltipContent={tooltipContent}
                offset={[10, 25]}
                maxWidth="170px"
              >
                <TWTDText>{`This Week ${
                  !isMobileView ? 'to Date' : ''
                }`}</TWTDText>
              </TippyToolTip>
              {thisWeekToDate && (
                <DateRange>
                  {`${formatDate(thisWeekToDate?.startDate, 'day', true)}
                  - ${formatDate(thisWeekToDate?.endDate, 'day', true)}`}
                </DateRange>
              )}
            </H3>
          ) : (
            <ContentLoader width={200} height={31}>
              <rect width="170" height="20" x="0" y="0" />
            </ContentLoader>
          )}
          <Box
            display="flex"
            flexDirection={isMobileView ? 'column' : 'row'}
            pt="0.625rem"
          >
            <MetricsWrapper mr="10%" as="ul">
              {mappedPlaylistData &&
                playlistKeyValueGroupItems &&
                playlistKeyValueGroupItems.map((item: any, index: number) => (
                  <KPIRedesignedListItem
                    isMobileView={isMobileView}
                    key={index + 'listItem'}
                    {...item}
                  />
                ))}
              {!playlistKeyValueGroupItems && (
                <KPIListLoader
                  isMobileView={isMobileView}
                  arrayCount={3}
                  width={175}
                />
              )}
            </MetricsWrapper>
            <Box as="ul">
              {mappedPlaylistData &&
                playlistCountSkiprateKeyValueGroupItems &&
                playlistCountSkiprateKeyValueGroupItems.map(
                  (item: any, index: number) => (
                    <KPIRedesignedListItem
                      isMobileView={isMobileView}
                      key={index + 'listItem'}
                      {...item}
                    />
                  )
                )}
              {!playlistCountSkiprateKeyValueGroupItems && (
                <KPIListLoader
                  isMobileView={isMobileView}
                  arrayCount={2}
                  width={175}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box flexBasis="50%">
          {playlistLWTDItems && !error ? (
            <H3 isMobileView={isMobileView}>
              <TippyToolTip
                enabled
                tooltipContent={tooltipContent}
                offset={[10, 25]}
                maxWidth="170px"
              >
                <TWTDText>{`Last Week ${
                  !isMobileView ? 'to Date' : ''
                }`}</TWTDText>
              </TippyToolTip>
              {lastWeek && (
                <DateRange>
                  {`${formatDate(lastWeek?.startDate, 'day', true)}
                  - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                </DateRange>
              )}
            </H3>
          ) : (
            <ContentLoader width={200} height={31}>
              <rect width="170" height="20" x="0" y="0" />
            </ContentLoader>
          )}
          <MetricsWrapper as="ul" pt="0.625rem">
            {mappedPlaylistData &&
              playlistLWTDItems &&
              playlistLWTDItems.map((item: any, index: number) => (
                <KPIRedesignedListItem key={index + 'listItem'} {...item} />
              ))}
            {!playlistLWTDItems && (
              <KPIListLoader
                isMobileView={isMobileView}
                arrayCount={3}
                width={175}
              />
            )}
          </MetricsWrapper>
        </Box>
      </Box>
    </KPIContent>
  );
};

export default PlaylistKPIList;
